import { useId } from '@sixfold/app-framework';
import { Button, ButtonLink, Modal, ModalFooter, ModalHeader, ModalMain, Spinner, Stack } from '@sixfold/common-ui';
import { Localized } from '@sixfold/localization-component';
import * as React from 'react';

import { ErrorBoundary } from '../../../components/error_boundary';
import { Toolbar, ToolbarRow, ToolbarRowSection } from '../../../components/toolbar';
import { IntercomArticleRoutes, useLocalizedHelpCenterArticle } from '../../../routes/intercom';
import { SearchInput } from '../../../search/components/search_input';
import { UserInCompany } from '../../entities';
import { CompanyUserForm } from '../company_user_form';

import * as styles from './styles.css';

export interface Props {
  addUser: (user: { firstName: string; lastName: string; email: string }) => Promise<UserInCompany | void>;
  isLoading: boolean;
  isAllowedToAddUsers: boolean;
  count: number;
  searchQuery?: string;
  onSearchChange?: (query: string | undefined) => void;
}

const AddUserModal = ({ addUser }: Pick<Props, 'addUser'>) => {
  const formId = useId();
  const [isAddingUserFormOpen, setIsAddingUserFormOpen] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const handleAddUserToCompany = React.useCallback(
    async (user: { firstName: string; lastName: string; email: string }) => {
      setIsSaving(true);
      await addUser(user);

      setIsSaving(false);
      setIsAddingUserFormOpen(false);
    },
    [addUser],
  );

  return (
    <Modal
      trigger={
        <Button size="small" kind="primary" iconStart="small-plus">
          <Localized id="company.users.header.addUser.button.title">Add user</Localized>
        </Button>
      }
      open={isAddingUserFormOpen}
      onToggle={setIsAddingUserFormOpen}>
      <ModalHeader title={<Localized id="company.users.header.addUser.title">Add a user</Localized>} />
      <ModalMain>
        <CompanyUserForm onSubmit={handleAddUserToCompany} id={formId} />
      </ModalMain>
      <ModalFooter>
        <Stack justifyContent="end">
          <Button kind="primary" size="medium" type="submit" form={formId} loading={isSaving}>
            <Localized id="company.users.form.submit.button.title">Add user</Localized>
          </Button>
        </Stack>
      </ModalFooter>
    </Modal>
  );
};

export const CompanyUsersHeader: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  addUser,
  isLoading,
  isAllowedToAddUsers,
  count,
  searchQuery,
  onSearchChange,
}) => {
  const [lastKnownCount, setLastKnownCount] = React.useState(count);
  React.useEffect(() => {
    if (!isLoading) {
      setLastKnownCount(count);
    }
  }, [count, isLoading]);

  const isSearching = isLoading && searchQuery !== undefined;
  const countToDisplay = isLoading ? lastKnownCount : count;

  const onClearSearch = React.useCallback(() => {
    onSearchChange?.(undefined);
  }, [onSearchChange]);

  const onUpdateSearch = React.useCallback(
    ({ value }: { value: string }) => {
      onSearchChange?.(value === '' ? undefined : value);
    },
    [onSearchChange],
  );

  const helpCenterArticleUrl = useLocalizedHelpCenterArticle(IntercomArticleRoutes.HELP_CENTER_ARTICLE_USER_ROLES);

  return (
    <ErrorBoundary>
      <Toolbar data-test-id="companyUsersHeaderToolbar">
        <ToolbarRow>
          <ToolbarRowSection>
            <Localized id="company.users.userSearch.placeholder" defaultValue="Search for user">
              {(placeholder) => (
                <SearchInput
                  value={searchQuery ?? ''}
                  placeholder={placeholder}
                  onChange={onUpdateSearch}
                  onClear={onClearSearch}
                  debounceTimeout={300}
                  size="small"
                  className={styles.searchContainer}
                />
              )}
            </Localized>
            {isSearching ? (
              <Spinner size="small" />
            ) : (
              <span>
                <Localized
                  id="company.users.count.text"
                  defaultValue="{count, plural, =1 {1 user} other {# users}}"
                  variables={{ count: countToDisplay }}>
                  users
                </Localized>
              </span>
            )}
          </ToolbarRowSection>
          <ToolbarRowSection>
            {isAllowedToAddUsers ? (
              <AddUserModal addUser={addUser} />
            ) : (
              <ButtonLink
                size="small"
                kind="subtle"
                href={helpCenterArticleUrl}
                target="_blank"
                rel="noopener noreferrer nofollow"
                iconStart="small-help-outline">
                <Localized id="tour.details.notifications.help">Help</Localized>
              </ButtonLink>
            )}
          </ToolbarRowSection>
        </ToolbarRow>
      </Toolbar>
    </ErrorBoundary>
  );
};
